/**
 * useTranscribeExistingRequest Hook
 * React Query hook for transcribing audio on an existing request
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { offlineRequestsKeys } from './useOfflineRequestsQuery';
import { requestsKeys } from './useRequestsQuery';
import { noteImportUpload } from '../../services/clientImportTiming';
import { transcriptionService } from '../../services/transcriptionService';

import type {
  TranscribeExistingRequestData,
  CreateRequestWithImportResponse,
} from '../../types/transcription';

export const useTranscribeExistingRequest = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateRequestWithImportResponse,
    Error,
    TranscribeExistingRequestData,
    { startedAt: number }
  >({
    mutationFn: (data: TranscribeExistingRequestData) =>
      transcriptionService.transcribeExistingRequest(data),

    // PERF-4: t0 ≈ the upload click (mutation start).
    onMutate: () => ({ startedAt: Date.now() }),

    onSuccess: (response, variables, context) => {
      // PERF-4: link t0 to the new job so the COMPLETED transition can report.
      noteImportUpload(response.requestId, response.jobId, context.startedAt);

      // Invalidate all request list queries to show updated PROCESSING stage
      queryClient.invalidateQueries({ queryKey: requestsKeys.all });
      queryClient.invalidateQueries({ queryKey: offlineRequestsKeys.lists() });

      // Invalidate the specific request detail to refetch with PROCESSING stage
      queryClient.invalidateQueries({
        queryKey: requestsKeys.detail(String(variables.requestId)),
      });

      console.log(
        `✅ Request ${response.requestId} transcription started with job ${response.jobId}`,
      );
    },

    onError: error => {
      console.error(
        '❌ Failed to start transcription for existing request:',
        error,
      );
    },
  });
};
